import { get, reject } from 'lodash';
import logger from 'Logger/logger';
import moment from 'moment';
import { constants } from '@clubmed/utils';
import { ICONS } from 'Components/utils/icon/icons';
import { getTransportIcon } from 'Components/widgets/transport-details/transportUtils';
import { tAlt } from 'Components/utils/booking/bookingUtils';
import { t } from 'Src/i18n';
import { hasChildcare, hasTransfer, isRecommendedTransport } from 'Utils/proposal/proposalUtils';
import { isWinterMountainResort } from 'Utils/resort/resortUtils';
const {
  PACKAGES: { JOYVIEW_ROOM_ONLY },
} = constants;
const BASE_PATH = '/booking/';
export const SVRL_PMNTS = 'svrl-pmnts';
export const shouldHideServicesPage = (selectedProposal: Proposal, product: Product): boolean => {
  return (
    (!hasChildcare(selectedProposal) &&
      !product.isJoyview &&
      !product.isUrbanOasis &&
      selectedProposal.additionalServices.childcares.length === 0) ||
    (product.isJoyview &&
      !selectedProposal.alternativePackages &&
      selectedProposal.packageId === JOYVIEW_ROOM_ONLY) ||
    (product.isUrbanOasis &&
      !selectedProposal.alternativePackages &&
      selectedProposal.packageId === JOYVIEW_ROOM_ONLY)
  );
};
export const shouldHideTransportPage = (selectedProposal: Proposal): boolean => {
  return !hasTransfer(selectedProposal) && !get(selectedProposal, 'transports');
};
type getFilteredBreadcrumbPagesReturnType = ReadonlyArray<BookingPageData>;
export const getStepIndex = (
  steps: getFilteredBreadcrumbPagesReturnType,
  bookingStep: string,
): number => steps.findIndex((step) => step.name === bookingStep);
// eslint-disable-next-line max-params
export const getFilteredBreadcrumbPages = ({
  selectedProposal,
  resort,
  i18n,
  isActivitiesViewFeatureFlipped,
  isOptionHoldOnlyFeatureFlipped,
  isPartiallyPaid = false,
  shouldHideActivitiesStepAbTest = false,
}: {
  selectedProposal: Proposal;
  resort: Product;
  i18n: CMSContent;
  isActivitiesViewFeatureFlipped: boolean;
  isOptionHoldOnlyFeatureFlipped: boolean;
  isPartiallyPaid: boolean;
  shouldHideActivitiesStepAbTest?: boolean;
}): getFilteredBreadcrumbPagesReturnType => {
  const bankingStep = {
    name: 'banking',
    eventPageName: 'banking',
    path: `${BASE_PATH}banking`,
    actionGA: 'banking',
    agenceType: 'NonDucroire',
    labelGA: 'validateBanking',
    basePriceGALabel: 'basePricePaymentBanking',
    icon: ICONS.CREDIT_CARD,
    showIcon: true,
    title: i18n.booking.banking.pageTitle,
    seoTitle: i18n.booking.banking.seo.title,
    gridSize: '',
  };

  if (isPartiallyPaid) {
    // the proposals should not be modified after an household has already paid
    return [bankingStep];
  }

  const isRecommendedTransportSelected =
    selectedProposal.transports &&
    isRecommendedTransport(selectedProposal.transports, selectedProposal.id);
  const activitiesView =
    isActivitiesViewFeatureFlipped &&
    !shouldHideActivitiesStepAbTest &&
    (selectedProposal.additionalServices.activities.some(
      (activity) => activity.productInfo?.isIncluded === false,
    ) ||
      selectedProposal.additionalServices.rentals.length > 0)
      ? {
          name: 'activities',
          path: `${BASE_PATH}activities`,
          actionGA: 'activities',
          labelGA: 'validateActivities',
          basePriceGALabel: 'basePriceActivities',
          icon: isWinterMountainResort(resort) ? ICONS.SKI : ICONS.GOLF,
          showIcon: true,
          title: i18n.booking.activities.pageTitle,
          seoTitle: i18n.booking.activities.seo.title,
          gridSize: 'large',
        }
      : null;
  const isJoyview = get(resort, 'isJoyview');
  const isUrbanOasis = get(resort, 'isUrbanOasis');
  const isUrbanOasisOrJoyview = isJoyview || isUrbanOasis;
  const breadcrumbPages = [
    {
      name: 'accommodation',
      path: `${BASE_PATH}accommodation`,
      eventPageName: 'accommodations',
      actionGA: 'accommodation',
      labelGA: 'validateAccommodation',
      basePriceGALabel: 'basePriceAccommodation',
      icon: ICONS.ACCOMODATIONS,
      showIcon: true,
      title: tAlt({
        cruiseKeyPath: 'booking.accommodation.pageTitleCruise',
        product: resort,
        resortKeyPath: 'booking.accommodation.pageTitle',
      }),
      seoTitle: i18n.booking.accommodation.seo.title,
      gridSize: 'large',
    },
    {
      name: 'transport',
      path: `${BASE_PATH}transport`,
      actionGA: 'transport',
      labelGA: 'validateTransport',
      ...(get(selectedProposal, 'transports')
        ? {
            icon: getTransportIcon(
              get(selectedProposal, 'transports.inbound'),
              get(selectedProposal, 'transports.outbound'),
            ),
            // eslint-disable-next-line no-nested-ternary
            title: isRecommendedTransportSelected
              ? i18n.booking.transport.transportPageTitle
              : i18n.booking.transport.additionalTransports.transportSelection,
            seoTitle: i18n.booking.transport.seo.title,
            basePriceGALabel: 'basePriceTransport',
          }
        : {
            icon: ICONS.TRANSFER,
            title: i18n.booking.transport.transferPageTitle,
            seoTitle: i18n.booking.transport.transfer.seo.title,
            basePriceGALabel: 'basePriceTransfer',
          }),
      showIcon: true,
      isToBeHidden: shouldHideTransportPage(selectedProposal),
      gridSize: 'large',
    },
    {
      name: 'services',
      path: `${BASE_PATH}services`,
      actionGA: isUrbanOasisOrJoyview ? 'allInclusive' : 'service',
      labelGA: isUrbanOasisOrJoyview ? 'validateAllInclusive' : 'validateServices',
      basePriceGALabel: 'basePriceServices',
      icon: isUrbanOasisOrJoyview ? ICONS.DATE_PLUS : ICONS.CHILDREN,
      showIcon: true,
      title: isUrbanOasisOrJoyview
        ? i18n.booking.services.pageTitleJoyview
        : i18n.booking.services.pageTitleChildcare,
      seoTitle: i18n.booking.services.seo.title,
      isToBeHidden: shouldHideServicesPage(selectedProposal, resort),
      gridSize: isJoyview ? 'small' : 'large',
    },
    {
      name: 'participants',
      path: `${BASE_PATH}participants`,
      actionGA: 'participant',
      labelGA: 'validateParticipants',
      basePriceGALabel: 'basePricePaymentParticipants',
      icon: ICONS.CONTACT,
      showIcon: true,
      title: i18n.booking.participants.pageTitle,
      seoTitle: i18n.booking.participants.seo.title,
      gridSize: 'large',
    },
    {
      name: 'summary',
      path: `${BASE_PATH}summary`,
      actionGA: 'summary',
      labelGA: 'validateSummary',
      basePriceGALabel: 'basePriceSummary',
      icon: ICONS.SUMMARY,
      showIcon: true,
      title: t('booking.summary.pageTitle'),
      seoTitle: i18n.booking.summary.seo.title,
      gridSize: 'medium',
    },
  ];

  if (activitiesView) {
    breadcrumbPages.splice(3, 0, activitiesView);
  }

  // adds the banking step if the optionHoldOnly featureFlip is disabled
  if (!isOptionHoldOnlyFeatureFlipped) {
    breadcrumbPages.push(bankingStep);
  }

  return reject(breadcrumbPages, 'isToBeHidden');
};
export const getAllProposalsIdWithAlternative = (proposal: Proposal) => {
  return [
    proposal.id,
    ...(proposal.alternativePackages
      ? proposal.alternativePackages.map((alternativePackage) => alternativePackage.proposalId)
      : []),
  ];
};
export const trackErrorOnAddService = (
  error: any,
  trackCallback: any,
  defaultValue: string,
): void => {
  if (error) {
    const errorCode = error?.errors?.[0]?.errorCode;
    trackCallback({
      labelGA: `error_${errorCode ? errorCode : defaultValue}`,
    });
  }
};
export const handleWrongUrl = (router: any) => {
  const proposalIds = router.query?.proposalIds;
  const bookingStep = router.query?.bookingStep;
  const date = router.query?.date;
  const today = new Date();
  const isExpiredDate = moment(date).isBefore(moment(today), 'day');

  if (!router.query || !proposalIds || proposalIds.length < 1 || !bookingStep || isExpiredDate) {
    // If the url misses proposalsIds or bookingStep, or has a date in the past
    // then, we should redirect to homepage instead of throwing a 500 error
    logger.error(
      'If the url misses proposalsIds or bookingStep, or has a date in the past, then we should redirect to homepage instead of throwing a 500 error',
    );
    router.push('/');
    throw new Error('Invalid booking paramerter');
  }
};
export const getDepartureDateRange = (daysBeforeDeparture: number): string => {
  if (daysBeforeDeparture <= 34) {
    return 'late_booking';
  }

  if (daysBeforeDeparture <= 90) {
    return 'middle_booking';
  }

  return 'early_booking';
};
export const hexToRgba = (hex: string, alpha: number = 1) => {
  if (hex === '' || hex === null || !hex.startsWith('#')) {
    hex = '#924833';
  }

  const matches = hex && hex.match(/\w\w/g);

  if (!matches) {
    return hex;
  }

  const [r, g, b] = matches.map((x) => parseInt(x, 16));
  return `rgba(${r},${g},${b},${alpha})`;
};
export const getExclusiveCollectionFromComfort = (
  comfort: number,
  hasECSpace: boolean,
): string | null | undefined => {
  if (hasECSpace) {
    return 'exclusive_collection_space';
  }

  if (comfort == null) {
    return undefined;
  }

  if (comfort < 5) {
    return 'no';
  }

  return 'exclusive_collection';
};
export const getExclusiveCollectionFromAccomodation = (
  hasECSpace: boolean,
  isEclusiveCollectionAccommodation: boolean,
): string => {
  if (isEclusiveCollectionAccommodation && hasECSpace) {
    return 'exclusive_collection_space';
  }

  if (isEclusiveCollectionAccommodation && !hasECSpace) {
    return 'exclusive_collection';
  }

  return 'no';
};
